/**
 * useEmailDomain Hook
 *
 * Purpose: Manage email domain operations for Super Admin domain setup
 *
 * Features:
 * - Fetch domain record by organisation + domain name
 * - Register domain with Cloudflare
 * - Verify nameservers
 * - Add subdomain A records
 * - Sync SES DNS records to Cloudflare
 * - Check AWS SES verification status
 *
 * Used in: Super Admin Onboarding Dialog - Domain Setup Tab
 */

import { useState, useCallback } from 'react';
import { toast } from '@/hooks/use-toast';
import { apiClient } from '@/api/client';

interface EmailDomain {
  id: string;
  organisation_id: string;
  domain: string;
  is_verified: boolean;
  verification_status: string;
  verification_token: string | null;
  dkim_tokens: string[] | null;
  cloudflare_zone_id: string | null;
  cloudflare_nameservers: string[] | null;
  cloudflare_zone_status: string | null;
  dns_records_synced: boolean;
  dns_records_synced_at: string | null;
  default_from_email: string;
  default_from_name: string;
  created_at: string;
  updated_at: string;
  organisation_name?: string;
}

interface RegisterDomainParams {
  organisationId: string;
  domain: string;
  defaultFromEmail: string;
  defaultFromName: string;
  userOnboardingId?: string;  // Optional - for nameserver sync during onboarding
}

interface AddSubdomainParams {
  domainId: string;
  subdomain: string;
  ipAddress: string;
  proxied?: boolean;
}

export const useEmailDomain = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  /**
   * Fetch domain record by organisation ID and domain name
   */
  const fetchDomainByOrganisation = useCallback(async (
    organisationId: string,
    domain: string
  ): Promise<EmailDomain | null> => {
    try {
      setLoading(true);
      setError(null);

      const response = await apiClient.get<{ success: boolean; domain: EmailDomain }>(
        `/api/admin/email/domains/by-organisation/${organisationId}/${domain}`
      );

      return response.data.domain;
    } catch (err: any) {
      // 404 means domain not registered yet - this is expected, not an error
      if (err.response?.status === 404) {
        return null;
      }
      console.error('Error fetching domain:', err);
      setError(err.message);
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Register domain with Cloudflare and AWS SES (creates new Cloudflare zone)
   */
  const registerDomain = useCallback(async (params: RegisterDomainParams): Promise<{
    success: boolean;
    domain?: EmailDomain;
    nameservers?: string[];
    error?: string;
  }> => {
    try {
      setLoading(true);
      setError(null);

      const response = await apiClient.post<any>('/api/admin/email/domains', {
        organisation_id: params.organisationId,
        domain: params.domain,
        default_from_email: params.defaultFromEmail,
        default_from_name: params.defaultFromName,
        user_onboarding_id: params.userOnboardingId  // Pass for nameserver sync
      });

      toast({
        title: "Domain Registered",
        description: `${params.domain} has been registered with Cloudflare and AWS SES`
      });

      return {
        success: true,
        domain: response.data.domain,
        nameservers: response.data.nameservers
      };
    } catch (err: any) {
      console.error('Error registering domain:', err);
      setError(err.message);

      toast({
        title: "Registration Failed",
        description: err.message,
        variant: "destructive"
      });

      return {
        success: false,
        error: err.message
      };
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Import domain that already exists in Cloudflare
   */
  const importExistingDomain = useCallback(async (params: RegisterDomainParams): Promise<{
    success: boolean;
    domain?: EmailDomain;
    nameservers?: string[];
    error?: string;
  }> => {
    try {
      setLoading(true);
      setError(null);

      const response = await apiClient.post<any>('/api/admin/email/domains/import-existing', {
        organisation_id: params.organisationId,
        domain: params.domain,
        default_from_email: params.defaultFromEmail,
        default_from_name: params.defaultFromName
      });

      toast({
        title: "Domain Imported",
        description: `${params.domain} found in Cloudflare and registered with AWS SES`
      });

      return {
        success: true,
        domain: response.data.domain,
        nameservers: response.data.nameservers
      };
    } catch (err: any) {
      console.error('Error importing domain:', err);
      setError(err.message);

      toast({
        title: "Import Failed",
        description: err.message,
        variant: "destructive"
      });

      return {
        success: false,
        error: err.message
      };
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Verify nameservers with Cloudflare
   */
  const verifyNameservers = useCallback(async (domainId: string): Promise<{
    success: boolean;
    status?: string;
    verified?: boolean;
    error?: string;
  }> => {
    try {
      setLoading(true);
      setError(null);

      const response = await apiClient.post<any>(
        `/api/admin/email/domains/${domainId}/verify-nameservers`
      );

      if (response.data.verified) {
        toast({
          title: "Nameservers Verified",
          description: "Cloudflare zone is now active"
        });
      } else {
        toast({
          title: "Verification Pending",
          description: "Nameservers are not active yet. This can take 24-48 hours.",
          variant: "default"
        });
      }

      return {
        success: true,
        status: response.data.status,
        verified: response.data.verified
      };
    } catch (err: any) {
      console.error('Error verifying nameservers:', err);
      setError(err.message);

      toast({
        title: "Verification Check Failed",
        description: err.message,
        variant: "destructive"
      });

      return {
        success: false,
        error: err.message
      };
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Add subdomain A record to Cloudflare
   */
  const addSubdomainRecord = useCallback(async (params: AddSubdomainParams): Promise<{
    success: boolean;
    record?: any;
    error?: string;
  }> => {
    try {
      setLoading(true);
      setError(null);

      const response = await apiClient.post<any>(
        `/api/admin/email/domains/${params.domainId}/subdomain`,
        {
          subdomain: params.subdomain,
          ip_address: params.ipAddress,  // API expects snake_case
          proxied: params.proxied || false
        }
      );

      // Backend returns { success, data: { subdomain, ipAddress, record }, message }
      const result = response.data.data || response.data;

      toast({
        title: "A Record Added",
        description: `Subdomain ${result.subdomain} has been configured`
      });

      return {
        success: true,
        record: result.record
      };
    } catch (err: any) {
      console.error('Error adding subdomain:', err);
      setError(err.message);

      toast({
        title: "Failed to Add A Record",
        description: err.message,
        variant: "destructive"
      });

      return {
        success: false,
        error: err.message
      };
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Sync SES DNS records to Cloudflare
   */
  const syncSESRecords = useCallback(async (domainId: string): Promise<{
    success: boolean;
    recordsCreated?: number;
    error?: string;
  }> => {
    try {
      setLoading(true);
      setError(null);

      const response = await apiClient.post<any>(
        `/api/admin/email/domains/${domainId}/sync-ses-records`
      );

      toast({
        title: "DNS Records Synced",
        description: `${response.data.recordsCreated} SES verification and DKIM records added to Cloudflare`
      });

      return {
        success: true,
        recordsCreated: response.data.recordsCreated
      };
    } catch (err: any) {
      console.error('Error syncing SES records:', err);
      setError(err.message);

      toast({
        title: "DNS Sync Failed",
        description: err.message,
        variant: "destructive"
      });

      return {
        success: false,
        error: err.message
      };
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Check AWS SES verification status
   */
  const checkVerification = useCallback(async (domainId: string): Promise<{
    success: boolean;
    isVerified?: boolean;
    verificationStatus?: string;
    error?: string;
  }> => {
    try {
      setLoading(true);
      setError(null);

      const response = await apiClient.post<any>(
        `/api/admin/email/domains/${domainId}/check-verification`
      );

      if (response.data.isVerified) {
        toast({
          title: "Domain Verified! ✅",
          description: "Domain is verified and ready for sending emails"
        });
      } else {
        toast({
          title: "Verification Pending",
          description: "Domain is not verified yet. DNS propagation can take 24-48 hours.",
          variant: "default"
        });
      }

      return {
        success: true,
        isVerified: response.data.isVerified,
        verificationStatus: response.data.verificationStatus
      };
    } catch (err: any) {
      console.error('Error checking verification:', err);
      setError(err.message);

      toast({
        title: "Verification Check Failed",
        description: err.message,
        variant: "destructive"
      });

      return {
        success: false,
        error: err.message
      };
    } finally {
      setLoading(false);
    }
  }, []);

  return {
    loading,
    error,
    fetchDomainByOrganisation,
    registerDomain,
    importExistingDomain,
    verifyNameservers,
    addSubdomainRecord,
    syncSESRecords,
    checkVerification
  };
};
